import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { setSelectedAccount } from '../../store/appSlice';
import { Account } from '../../types/schema';
import { track as trackAmplitude } from '../../services/amplitude';
import carbonSpaceLogo from '../../assets/logo.png';

const LogoImage = styled('img')(({ theme }) => ({
  width: 80,
  marginBottom: theme.spacing(2),
}));

interface AccountSwitcherModalProps {
  open: boolean;
  onClose: () => void;
}

const AccountSwitcherModal: React.FC<AccountSwitcherModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { selectedAccount, availableAccounts } = useSelector((state: RootState) => state.app);
  const [selectedAccountId, setSelectedAccountId] = useState<number | ''>(
    selectedAccount?.accountId || ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccountChange = () => {
    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    const account = availableAccounts.find((acc: Account) => acc.accountId === selectedAccountId);
    if (account) {
      const accountChanged = selectedAccount?.accountId !== account.accountId;
      setIsLoading(true);
      dispatch(setSelectedAccount(account));

      // Track account switch
      if (accountChanged) {
        trackAmplitude('Account Switched', {
          account_id: account.accountId,
          account_name: account.name,
          previous_account_id: selectedAccount?.accountId,
        });
      }

      // Reload app if account changed to refetch all account-related data
      if (accountChanged) {
        // Wait briefly for state to be persisted before reloading
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        setIsLoading(false);
        onClose();
      }
    } else {
      setError('Selected account not found');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAccountChange();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <LogoImage src={carbonSpaceLogo} alt="CarbonSpace Logo" />

        <DialogTitle sx={{ p: 0, mb: 2, typography: 'h5', fontWeight: 'bold' }}>
          Switch Account
        </DialogTitle>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Select the account you want to work with
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth margin="normal" disabled={isLoading}>
          <InputLabel>Select Account</InputLabel>
          <Select
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value as number)}
            label="Select Account"
            onKeyPress={handleKeyPress}
          >
            {availableAccounts.map((account: Account) => (
              <MenuItem key={account.accountId} value={account.accountId}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
          sx={{ mr: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAccountChange}
          disabled={isLoading || !selectedAccountId}
          sx={{ textTransform: 'none' }}
        >
          {isLoading ? 'Switching...' : 'Switch Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountSwitcherModal;
